import { ColumnDef, Row } from "@tanstack/react-table";
import { Category } from "@/app/types/category";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Edit, Trash2 } from "lucide-react";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "react-router-dom";

// Define filterable columns for the data table
export const filterableColumns = (
  allCategories: Category[] // Function to get dynamic options
): { id: string; title: string; options: { label: string; value: string }[] }[] => {
  // Ensure allCategories is an array
  const safeCategories = Array.isArray(allCategories) ? allCategories : [];
  
  return [
    {
      id: "is_active",
      title: "Status",
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
    },
    {
      id: "parent_id",
      title: "Parent Category",
      options: [
        { label: "Top Level", value: "null" },
        ...safeCategories
          .filter((cat) => cat.parent_id === null) // Only show top-level as filterable parents for simplicity
          .map((cat) => ({
            label: cat.name,
            value: cat.id.toString(),
          })),
      ],
    },
  ];
};

export const columns = (allCategories: Category[]): ColumnDef<Category>[] => {
  // Ensure allCategories is an array
  const safeCategories = Array.isArray(allCategories) ? allCategories : [];
  
  return [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => {
        const category = row.original;
        return (
          <Link 
            to={`/settings/categories/detail/${category.id}`}
            className="font-medium hover:underline text-primary"
          >
            {category.name}
          </Link>
        );
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Description" />,
      cell: ({ row }) => row.original.description || "-",
    },
    {
      accessorKey: "parent_id",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Parent Category" />,
      cell: ({ row }): JSX.Element | string => {
        const parentId = row.original.parent_id;
        const parent = safeCategories.find((cat) => cat.id === parentId);
      return parent ? (
        <Badge variant="outline">{parent.name}</Badge>
      ) : (
        <span className="text-muted-foreground">Top Level</span>
      );
    },
    filterFn: (row: Row<Category>, id: string, value: string[]): boolean => {
      if (!value.length) return true;
      const parentId = row.original.parent_id;
      if (value.includes("null") && parentId === null) return true;
      if (parentId && value.includes(parentId.toString())) return true;
      return false;
    },
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => (
      row.original.is_active ? (
        <Badge className="bg-green-600">Active</Badge>
      ) : (
        <Badge variant="destructive">Inactive</Badge>
      )
    ),
    filterFn: (row: Row<Category>, id: string, value: string[]): boolean => {
      if (!value.length) return true;
      const isActive = row.original.is_active;
      return value.includes(isActive ? "true" : "false");
    },
  },
  {
    id: "actions",
    cell: ({ row }): JSX.Element => {
      const category = row.original;
      
      // Custom event dispatcher
      const dispatchEvent = (eventName: string, detail: Category): void => {
        const event = new CustomEvent(eventName, { detail });
        document.dispatchEvent(event);
      };
      
      return (
        <div className="flex items-center justify-end gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => dispatchEvent("view-category", category)}
                >
                  <Eye className="h-4 w-4" />
                  <span className="sr-only">View</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View details</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => dispatchEvent("edit-category", category)}
                >
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit category</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => dispatchEvent("delete-category", category)}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete category</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  ];
}; 